import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPencilSquare,
    faGear,
    faMicrochip,
    faFileLines,
    faPlus,
    faCloudUploadAlt,
    faSave,
    faSpinner,
    faHistory,
    faArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import patientService from '../../../../services/patientService';
import practiceService from '../../../../services/practiceService';
import inventoryService from '../../../../services/inventoryService';
import ScheduleTargetSettings from '../../ScheduleTargetSettings/ScheduleTargetSettings';
import ActionButtons from '../../../../components/Common/ActionButtons/ActionButtons';
import StatusBadge from '../../../../components/Common/StatusBadge/StatusBadge';
import { PatientAscStyleSinglePicker, PatientAscStyleMultiPicker, buildCaregiverOptionsWithStubs } from '../shared/PatientCaregiverPickers';
import { useBreadcrumb } from '../../../../contexts/BreadcrumbContext';
import PatientsLogModal, { PatientsLogContent } from '../../../../components/PatientsLogModal/PatientsLogModal';
import { TIME_ZONES, normalizeTimeZone } from '../../../../utils/constants';
import { buildPatientProfileImageUrl } from '../../../../utils/patientProfileImage.js';
import PageLoadingSpinner from '../../../../components/PageLoadingSpinner/PageLoadingSpinner';
import '../../PracticesList/PracticesList.css';
import './PatientEditView.css';
import '../AddPatient/AddPatient.css';

/**
 * PatientEditView — `/practices/:practiceId/patients/:patientId/edit` (opened from patient profile).
 * Not the same as AddPatient with `?editId=` (patient list single-page edit).
 */

const LOG_MODULE_PATIENT = 'Patient';
const LOG_MODULE_SCHEDULE = 'Patient Schedule';
const LOG_MODULE_METER = 'Patient Meter';
const LOG_MODULE_DOCUMENT = 'Patient Document';

const METER_EXTENSION_OPTIONS = [
    { value: '1', label: 'User 1' },
    { value: '2', label: 'User 2' },
    { value: '3', label: 'User 3' },
    { value: '4', label: 'User 4' },
    { value: 'A', label: 'All' }
];

/** Normalize meter rows from /meters API or getPatient meterMappings (mixed casing). */
function normalizeMappedDeviceRows(rows) {
    if (!Array.isArray(rows)) return [];
    return rows
        .filter((m) => m && (m.id != null || m.serial_number || m.serialNumber || m.model_number || m.modelNumber))
        .map((m) => ({
            id: m.id,
            model_number: m.model_number ?? m.modelNumber ?? '',
            extension: m.extension ?? '',
            serial_number: m.serial_number ?? m.serialNumber ?? '',
            gateway_serial_number: m.gateway_serial_number ?? m.gatewaySerialNumber ?? ''
        }));
}

/** Axios may return `{ data: Product[] }` or a raw array from some callers. */
function normalizeProductsDropdownResponse(res) {
    if (Array.isArray(res)) return res;
    if (res && Array.isArray(res.data)) return res.data;
    return [];
}

function normalizeArrayFromApiResponse(response, primaryKey) {
    const data = response?.data;
    if (Array.isArray(data?.data?.[primaryKey])) return data.data[primaryKey];
    if (Array.isArray(data?.[primaryKey])) return data[primaryKey];
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data)) return data;
    return [];
}

function mappedDevicesToMeterMappingsPayload(rows) {
    if (!Array.isArray(rows)) return [];
    const mapped = rows
        .filter(
            (r) =>
                String(r.model_number || '').trim() &&
                String(r.extension || '').trim() &&
                String(r.serial_number || '').trim()
        )
        .map((r) => ({
            modelNumber: String(r.model_number).trim(),
            extension: String(r.extension).trim(),
            serialNumber: String(r.serial_number).trim(),
            gatewaySerialNumber: String(r.gateway_serial_number || '').trim()
        }));
    const seen = new Set();
    return mapped.filter((r) => {
        const k = `${r.modelNumber}|${r.extension}|${r.serialNumber}`.toLowerCase();
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
    });
}

function normalizeInsurancePayload(data) {
    return {
        primary: {
            insuranceName: String(data?.primary?.insuranceName || '').trim(),
            subscriberId: String(data?.primary?.subscriberId || '').trim(),
            insuredName: String(data?.primary?.insuredName || '').trim(),
            groupNo: String(data?.primary?.groupNo || '').trim()
        },
        secondary: {
            insuranceName: String(data?.secondary?.insuranceName || '').trim(),
            subscriberId: String(data?.secondary?.subscriberId || '').trim(),
            insuredName: String(data?.secondary?.insuredName || '').trim(),
            groupNo: String(data?.secondary?.groupNo || '').trim()
        }
    };
}

function normalizeMetersForCompare(rows) {
    return (mappedDevicesToMeterMappingsPayload(rows) || [])
        .map((m) => ({
            modelNumber: String(m.modelNumber || '').trim(),
            extension: String(m.extension || '').trim(),
            serialNumber: String(m.serialNumber || '').trim(),
            gatewaySerialNumber: String(m.gatewaySerialNumber || '').trim()
        }))
        .sort((a, b) => `${a.modelNumber}|${a.extension}|${a.serialNumber}`.localeCompare(`${b.modelNumber}|${b.extension}|${b.serialNumber}`));
}

function normalizeMonitoringForCompare(types, values) {
    const out = [];
    if (types?.bloodPressure) out.push({ type: 'bloodPressure', icdCode: String(values?.bloodPressure || 'I50.1').trim() || 'I50.1' });
    if (types?.bloodGlucose) out.push({ type: 'bloodGlucose', icdCode: String(values?.bloodGlucose || 'R73.0').trim() || 'R73.0' });
    if (types?.weight) out.push({ type: 'weight', icdCode: String(values?.weight || 'ICD-10').trim() || 'ICD-10' });
    return out.sort((a, b) => a.type.localeCompare(b.type));
}

function payloadValueForCompare(key, value) {
    if (key === 'practice_caregiver_ids' || key === 'system_caregiver_ids') {
        return (Array.isArray(value) ? value : [])
            .map((n) => Number(n))
            .filter((n) => Number.isFinite(n) && n > 0)
            .sort((a, b) => a - b);
    }
    if (key === 'meter_mappings' || key === 'monitoring_types' || key === 'insurance_data') {
        return value;
    }
    return value == null ? '' : String(value);
}

function buildPatientUpdatePayload(formData, monitoringTypes, monitoringValues, insuranceData, mappedDevices) {
    const systemCaregiverIdsNum = (formData.systemCaregiverIds || [])
        .map((id) => parseInt(id, 10))
        .filter((n) => !Number.isNaN(n) && n > 0);
    const practiceCaregiverIdsNum = (formData.practiceCaregiverIds || [])
        .map((id) => parseInt(id, 10))
        .filter((n) => !Number.isNaN(n) && n > 0);
    const resolvedStateId = STATE_ABBR_TO_ID[formData.state] ?? parseInt(formData.state, 10);

    return {
        firstName: String(formData.firstName || '').trim(),
        middleName: String(formData.middleInitial || '').trim(),
        lastName: String(formData.lastName || '').trim(),
        username: String(formData.username || '').trim(),
        email: String(formData.email || '').trim(),
        title: String(formData.title || '').trim(),
        gender: String(formData.gender || '').trim(),
        dateOfBirth: String(formData.dateOfBirth || '').trim(),
        homePhone: String(formData.homePhone || '').trim(),
        cellPhone: String(formData.cellPhone || '').trim(),
        fax: String(formData.fax || '').trim(),
        addressLine1: String(formData.addressLine1 || '').trim(),
        addressLine2: String(formData.addressLine2 || '').trim(),
        city: String(formData.city || '').trim(),
        state: String(formData.state || '').trim(),
        state_id: Number.isFinite(resolvedStateId) && resolvedStateId > 0 ? resolvedStateId : null,
        zipCode: String(formData.zipCode || '').trim(),
        status: String(formData.status || '').trim(),
        timeZone: String(formData.timeZone || '').trim(),
        rpmStartDate: String(formData.rpmStartDate || '').trim(),
        provider: String(formData.provider || '').trim(),
        practice_caregiver_ids: practiceCaregiverIdsNum,
        system_caregiver_ids: systemCaregiverIdsNum,
        is_two_way_auth: formData.twoWayAuth === 'Enabled' ? 1 : 0,
        monitoring_types: normalizeMonitoringForCompare(monitoringTypes, monitoringValues),
        meter_mappings: normalizeMetersForCompare(mappedDevices),
        insurance_data: normalizeInsurancePayload(insuranceData)
    };
}

function diffUpdatePayload(originalPayload, currentPayload) {
    const changed = {};
    const keys = new Set([...Object.keys(originalPayload || {}), ...Object.keys(currentPayload || {})]);
    keys.forEach((key) => {
        const left = payloadValueForCompare(key, originalPayload?.[key]);
        const right = payloadValueForCompare(key, currentPayload?.[key]);
        if (JSON.stringify(left) !== JSON.stringify(right)) {
            changed[key] = currentPayload?.[key];
        }
    });
    return changed;
}

const US_STATES = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

function formatDate(dateString) {
    if (!dateString) return '-';
    try {
        const d = new Date(dateString);
        if (isNaN(d.getTime())) return '-';
        return d.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    } catch {
        return '-';
    }
}

const STATE_ID_TO_ABBR = Object.fromEntries(
    US_STATES.map((abbr, i) => [i + 1, abbr])
);
const STATE_ABBR_TO_ID = Object.fromEntries(
    US_STATES.map((abbr, i) => [abbr, i + 1])
);

function formatPhoneDisplay(value) {
    if (!value) return '';
    const phoneNumber = String(value).replace(/\D/g, '');
    if (phoneNumber.length < 4) return phoneNumber;
    if (phoneNumber.length < 7) {
        return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)} - ${phoneNumber.slice(6, 10)}`;
}

function isValidEmailFormat(email) {
    const s = String(email ?? '').trim();
    if (!s) return false;
    if (/\s/.test(s)) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

function stateSelectValueFromPatient(p) {
    const sid = p?.state_id ?? p?.address?.state_id;
    if (sid == null || sid === '') return '';
    const n = parseInt(String(sid), 10);
    if (!Number.isNaN(n) && STATE_ID_TO_ABBR[n]) return STATE_ID_TO_ABBR[n];
    const u = String(sid).toUpperCase();
    return US_STATES.includes(u) ? u : '';
}

/** Split API ids that were stored as one comma-separated string (e.g. `['123,456,789']`). */
function expandCaregiverIdsFromApi(raw, singleFallback) {
    const parts = [];
    const pushToken = (t) => {
        const s = String(t).trim();
        if (!s) return;
        if (s.startsWith('[')) {
            try {
                const inner = JSON.parse(s);
                if (Array.isArray(inner)) {
                    inner.forEach((x) => pushToken(x));
                    return;
                }
            } catch {
                /* fall through */
            }
        }
        if (s.includes(',')) s.split(',').forEach((p) => pushToken(p.trim()));
        else parts.push(s.replace(/^\[|\]$/g, ''));
    };
    if (Array.isArray(raw) && raw.length > 0) {
        raw.forEach((x) => pushToken(x));
        return [...new Set(parts.filter(Boolean))];
    }
    const one = singleFallback;
    if (one != null && String(one).trim() !== '') {
        pushToken(one);
        return [...new Set(parts.filter(Boolean))];
    }
    return [];
}

/** Practice `selected_monitoring_types` may be number[], string[], or a JSON string — normalize for `.includes(1)` etc. */
function normalizePracticeMonitoringTypes(raw) {
    if (!raw) return [];
    let arr = raw;
    if (typeof raw === 'string') {
        try {
            arr = JSON.parse(raw);
        } catch {
            arr = raw.split(',').map((s) => s.trim());
        }
    }
    if (!Array.isArray(arr)) return [];
    return [...new Set(arr.map((x) => parseInt(String(x), 10)).filter((n) => !Number.isNaN(n) && n > 0))];
}

function normalizePatientMonitoringTypes(raw) {
    if (!raw) return [];
    let parsed = raw;
    if (typeof parsed === 'string') {
        try {
            parsed = JSON.parse(parsed);
        } catch {
            parsed = parsed.split(',').map((s) => s.trim());
        }
    }
    if (!Array.isArray(parsed)) return [];
    return [...new Set(parsed.map((x) => parseInt(String(x), 10)).filter((n) => !Number.isNaN(n) && n > 0))];
}

export default function PatientEditView() {
    const { practiceId, patientId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

    const [activeTab, setActiveTab] = useState('patient-info');
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Data State
    const [patientData, setPatientData] = useState(null);
    const [practiceProviders, setPracticeProviders] = useState([]);
    const [practiceCaregivers, setPracticeCaregivers] = useState([]);
    const [systemCaregivers, setSystemCaregivers] = useState([]);
    const [products, setProducts] = useState([]);
    const [mappedDevices, setMappedDevices] = useState([]);
    const [uploadedDocuments, setUploadedDocuments] = useState([]);

    // Form State - Tab 1: Edit Patient
    const [formData, setFormData] = useState({
        title: '', firstName: '', middleInitial: '', lastName: '', username: '',
        addressLine1: '', addressLine2: '', city: '', state: '', zipCode: '',
        homePhone: '', cellPhone: '', fax: '', email: '',
        gender: 'Male', dateOfBirth: '', twoWayAuth: 'Enabled', status: 'Active',
        timeZone: '', rpmStartDate: '', provider: '', systemCaregiverIds: [], practiceCaregiverIds: [],
        accountNumber: ''
    });

    const systemCaregiverPickerOptions = useMemo(
        () => buildCaregiverOptionsWithStubs(systemCaregivers, formData.systemCaregiverIds),
        [systemCaregivers, formData.systemCaregiverIds]
    );
    const practiceCaregiverPickerOptions = useMemo(
        () => buildCaregiverOptionsWithStubs(practiceCaregivers, formData.practiceCaregiverIds),
        [practiceCaregivers, formData.practiceCaregiverIds]
    );

    const [fieldErrors, setFieldErrors] = useState({});

    // Form State - Monitoring & Insurance (Part of Tab 1)
    const [monitoringTypes, setMonitoringTypes] = useState({ bloodPressure: false, bloodGlucose: false, weight: false });
    const [monitoringValues, setMonitoringValues] = useState({ bloodPressure: 'I50.1', bloodGlucose: 'R73.0', weight: 'ICD-10' });
    const [practiceMonitoringTypes, setPracticeMonitoringTypes] = useState([]);
    const [insuranceData, setInsuranceData] = useState({
        primary: { insuranceName: '', subscriberId: '', insuredName: '', groupNo: '' },
        secondary: { insuranceName: '', subscriberId: '', insuredName: '', groupNo: '' }
    });

    // Form State - Tab 3: Manage Meter
    const [meterForm, setMeterForm] = useState({ modelNumber: '', extension: '', serialNumber: '', gatewaySerialNumber: '' });

    // Form State - Tab 4: Uploaded Documents
    const [docUpload, setDocUpload] = useState({ name: '', file: null });
    const fileInputRef = useRef(null);

    const [patientsLogModule, setPatientsLogModule] = useState(LOG_MODULE_PATIENT);
    const [patientsLogSection, setPatientsLogSection] = useState(null);
    const [previousTab, setPreviousTab] = useState('patient-info');
    const [confirmDeleteMeterRow, setConfirmDeleteMeterRow] = useState(null);
    const [confirmDeleteDoc, setConfirmDeleteDoc] = useState(null);

    const documentsLoadedForPatientRef = useRef(null);
    const metersLoadedForPatientRef = useRef(null);
    const productsLoadedForPatientRef = useRef(null);
    const prevActiveTabRef = useRef(null);
    const initialUpdatePayloadRef = useRef(null);

    const openPatientsLog = (moduleId, section = null) => {
        setPatientsLogModule(moduleId);
        setPatientsLogSection(section);
        setPreviousTab(activeTab);
        setActiveTab('event-logs');
    };

    useEffect(() => {
        documentsLoadedForPatientRef.current = null;
        metersLoadedForPatientRef.current = null;
        productsLoadedForPatientRef.current = null;
        prevActiveTabRef.current = null;
    }, [patientId]);

    useEffect(() => {
        loadPatientInformationCore();
        // location.key: refetch when returning to this screen (parity with AddPatient edit load).
    }, [practiceId, patientId, location.key]);

    // Whenever user returns to Patient Info from another tab, reload time zone from the server.
    useEffect(() => {
        const prev = prevActiveTabRef.current;
        prevActiveTabRef.current = activeTab;
        if (activeTab !== 'patient-info' || !practiceId || !patientId) return;
        const switchedFromOtherTab = prev != null && prev !== 'patient-info';
        if (!switchedFromOtherTab) return;

        let cancelled = false;
        (async () => {
            try {
                const pRes = await patientService.getPatient(practiceId, patientId);
                if (cancelled || !pRes.data?.success) return;
                const raw = pRes.data.data?.time_zone ?? pRes.data.data?.timeZone;
                const tz = normalizeTimeZone(raw);
                setFormData((prevForm) => ({ ...prevForm, timeZone: tz }));
            } catch {
                /* ignore */
            }
        })();
        return () => { cancelled = true; };
    }, [activeTab, practiceId, patientId]);

    useEffect(() => () => clearBreadcrumbs(), [clearBreadcrumbs]);

    useEffect(() => {
        if (loading || !patientData) return;
        const practiceName = patientData.practice_name || 'Practice';
        const patientCrumbName = `${patientData.last_name || ''}${patientData.last_name ? ', ' : ''}${patientData.first_name || ''}`.trim() || 'Patient';
        // Practice → Patients → [name] → Edit patient (tabs do not add breadcrumb levels)
        updateBreadcrumbs([
            { label: 'Practices', path: '/practices' },
            { label: practiceName, path: `/practices/${practiceId}` },
            { label: 'Patients', path: `/practices/${practiceId}?tab=patient` },
            { label: patientCrumbName, path: `/practices/${practiceId}/patients/${patientId}` },
            { label: 'Edit patient', path: null, active: true }
        ]);
    }, [loading, patientData, practiceId, patientId, updateBreadcrumbs]);

    // Documents: lazy-load only when Uploaded Documents tab opens.
    useEffect(() => {
        if (!practiceId || !patientId) return;
        if (activeTab !== 'uploaded-documents') return;
        if (documentsLoadedForPatientRef.current === patientId) return;

        let cancelled = false;
        (async () => {
            try {
                const docsRes = await patientService.getPatientDocuments(practiceId, patientId);
                if (!cancelled) {
                    setUploadedDocuments(docsRes.data?.data || []);
                    documentsLoadedForPatientRef.current = patientId;
                }
            } catch {
                /* ignore */
            }
        })();
        return () => { cancelled = true; };
    }, [activeTab, practiceId, patientId]);

    // Meter Mapping: lazy-load meters and products only when the tab opens.
    useEffect(() => {
        if (!practiceId || !patientId || activeTab !== 'meter-mapping') return;
        const shouldLoadMeters = metersLoadedForPatientRef.current !== patientId;
        const shouldLoadProducts = productsLoadedForPatientRef.current !== patientId;
        if (!shouldLoadMeters && !shouldLoadProducts) return;

        let cancelled = false;
        (async () => {
            try {
                const requests = [];
                if (shouldLoadMeters) requests.push(patientService.getPatientMeters(practiceId, patientId));
                if (shouldLoadProducts) requests.push(inventoryService.getProductsForDropdown());
                const results = await Promise.all(requests);
                if (cancelled) return;

                let idx = 0;
                if (shouldLoadMeters) {
                    const metersRes = results[idx++];
                    const rawMeters = metersRes?.data?.data || [];
                    setMappedDevices(normalizeMappedDeviceRows(rawMeters));
                    metersLoadedForPatientRef.current = patientId;

                    // Keep "original payload" aligned so unchanged meter rows don't appear as edits.
                    if (initialUpdatePayloadRef.current) {
                        initialUpdatePayloadRef.current = {
                            ...initialUpdatePayloadRef.current,
                            meter_mappings: normalizeMetersForCompare(rawMeters)
                        };
                    }
                }
                if (shouldLoadProducts) {
                    const productsRes = results[idx++];
                    setProducts(normalizeProductsDropdownResponse(productsRes));
                    productsLoadedForPatientRef.current = patientId;
                }
            } catch (e) {
                console.error('Meter tab products load failed:', e);
                toast.error('Failed to load products for meter mapping');
            }
        })();
        return () => { cancelled = true; };
    }, [activeTab, practiceId, patientId]);

    const patientProfilePicUrl = useMemo(
        () => buildPatientProfileImageUrl(patientData?.profile_pic),
        [patientData?.profile_pic]
    );

    const practiceLogoUrl = useMemo(() => {
        const raw = patientData?.practice_logo;
        if (!raw) return null;
        if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
        const baseUrl = API_URL.replace('/api', '');
        const logoPath = raw.startsWith('/') ? raw : (raw.startsWith('uploads') ? `/${raw}` : `/uploads/practice-logos/${raw}`);
        return `${baseUrl}${logoPath}`;
    }, [patientData?.practice_logo]);

    const [practiceLogoFailed, setPracticeLogoFailed] = useState(false);
    useEffect(() => {
        setPracticeLogoFailed(false);
    }, [practiceLogoUrl]);

    const loadPatientInformationCore = async () => {
        try {
            setLoading(true);
            // Patient core is mandatory; supporting datasets should not block the screen if they fail.
            const pRes = await patientService.getPatient(practiceId, patientId);
            const [providersRes, caregiversRes, sysRes, practiceRes] = await Promise.all([
                practiceService.getPracticeProviders(practiceId, { minimal: true }).catch(() => ({ data: { data: [] } })),
                practiceService.getPracticeCaregivers(practiceId, { minimal: true }).catch(() => ({ data: { data: [] } })),
                practiceService.getSystemCaregivers(practiceId).catch(() => ({ data: { data: [] } })),
                practiceService.getPractice(practiceId).catch(() => ({ data: { data: {} } }))
            ]);

            if (pRes.data.success) {
                const p = pRes.data.data;
                setPatientData(p);

                // Populate formData
                // Map status integer to string
                const statusMap = { 2: 'Active', 3: 'Pending', 4: 'Locked' };
                const statusValue = typeof p.status === 'number' ? (statusMap[p.status] || 'Active')
                    : (typeof p.status === 'string' && ['Active', 'Pending', 'Locked'].includes(p.status) ? p.status : 'Active');

                // Map gender — backend already converts to string in getPatient, but handle edge cases
                let genderValue = p.gender || 'Male';
                if (p.gender === 1) genderValue = 'Male';
                else if (p.gender === 2) genderValue = 'Female';
                else if (p.gender === 3) genderValue = 'Other';

                const rawHome = p.homePhone || p.phone || p.phone_number || '';
                const rawCell = p.cellPhone || p.cell_phone || p.cell_phone_number || '';
                const homeDigits = rawHome ? String(rawHome).replace(/\D/g, '') : '';
                const cellDigits = rawCell ? String(rawCell).replace(/\D/g, '') : '';

                const titleFromApi = (() => {
                    const tid = p.title_id ?? p.title;
                    if (tid == null || tid === '') return '';
                    const n = Number(tid);
                    if (Number.isFinite(n) && n > 0) return String(n);
                    return String(tid);
                })();
                const middleFromApi = (p.middle_name != null && p.middle_name !== '')
                    ? String(p.middle_name).trim().slice(0, 1)
                    : '';

                setFormData({
                    title: titleFromApi,
                    firstName: p.first_name || '',
                    middleInitial: middleFromApi,
                    lastName: p.last_name || '',
                    username: p.username || '',
                    addressLine1: p.address_line_1 || p.address?.address_line_1 || '',
                    addressLine2: p.address_line_2 || p.address?.address_line_2 || '',
                    city: p.city || p.address?.city || '',
                    state: stateSelectValueFromPatient(p),
                    zipCode: p.zip_code != null || p.address?.zip_code != null ? String(p.zip_code ?? p.address?.zip_code ?? '') : '',
                    homePhone: homeDigits ? formatPhoneDisplay(homeDigits) : '',
                    cellPhone: cellDigits ? formatPhoneDisplay(cellDigits) : '',
                    fax: p.fax || '',
                    email: p.email || '',
                    gender: genderValue,
                    dateOfBirth: p.date_of_birth ? p.date_of_birth.split('T')[0] : '',
                    twoWayAuth: p.is_two_way_auth === 1 ? 'Enabled' : 'Disabled',
                    status: statusValue,
                    timeZone: normalizeTimeZone(p.time_zone ?? p.timeZone),
                    rpmStartDate: p.rpm_start_date ? p.rpm_start_date.split('T')[0] : '',
                    provider: p.provider_id ? String(p.provider_id) : '',
                    systemCaregiverIds: expandCaregiverIdsFromApi(
                        p.system_caregiver_ids || p.systemCaregiverIds,
                        p.system_caregiver_id ?? p.system_caregiver?.id
                    ),
                    practiceCaregiverIds: (() => {
                        const fromJunction = expandCaregiverIdsFromApi(
                            p.practice_caregiver_ids || p.practiceCaregiverIds,
                            null
                        );
                        const fromColumn = expandCaregiverIdsFromApi(null, p.caregiver_id);
                        const seen = new Set();
                        const out = [];
                        for (const id of [...fromJunction, ...fromColumn]) {
                            if (!id || seen.has(id)) continue;
                            seen.add(id);
                            out.push(String(id));
                        }
                        if (out.length > 0) return out;
                        const one = p.practice_caregiver_id ?? p.practiceCaregiver?.id;
                        return one != null && String(one).trim() !== '' ? [String(one)] : [];
                    })(),
                    accountNumber: p.id ? String(p.id) : (p.patient_id || p.account_number || '')
                });

                // Populate Monitoring — handle both object and array formats
                const mTypes = p.monitoring_types || p.monitoringTypes || {};
                const mVals = p.monitoring_values || p.monitoringValues || {};
                const newTypes = { bloodPressure: false, bloodGlucose: false, weight: false };
                const newValues = { bloodPressure: 'I50.1', bloodGlucose: 'R73.0', weight: 'ICD-10' };

                if (Array.isArray(mTypes)) {
                    // Array format: [{type: 'bloodPressure', icdCode: '...'}, ...]
                    mTypes.forEach(m => {
                        if (m.type === 'bloodPressure' || m.type === 1) { newTypes.bloodPressure = true; newValues.bloodPressure = m.icdCode || m.icd_code || 'I50.1'; }
                        if (m.type === 'bloodGlucose' || m.type === 2) { newTypes.bloodGlucose = true; newValues.bloodGlucose = m.icdCode || m.icd_code || 'R73.0'; }
                        if (m.type === 'weight' || m.type === 3) { newTypes.weight = true; newValues.weight = m.icdCode || m.icd_code || 'ICD-10'; }
                    });
                } else if (typeof mTypes === 'object' && mTypes !== null) {
                    // Object format: { bloodPressure: true/false, bloodGlucose: true/false, weight: true/false }
                    newTypes.bloodPressure = !!mTypes.bloodPressure;
                    newTypes.bloodGlucose = !!mTypes.bloodGlucose;
                    newTypes.weight = !!mTypes.weight;
                    // Populate ICD values from monitoring_values
                    if (mVals.bloodPressure) newValues.bloodPressure = mVals.bloodPressure;
                    if (newTypes.bloodGlucose) newValues.bloodGlucose = mVals.bloodGlucose || 'R73.0';
                    if (mVals.weight) newValues.weight = mVals.weight;
                }

                // Numeric / serialized fallback format: [1,2,3], "1,2", or JSON strings from older payloads.
                const normalizedTypeIds = normalizePatientMonitoringTypes(mTypes);
                if (normalizedTypeIds.includes(1)) newTypes.bloodPressure = true;
                if (normalizedTypeIds.includes(2)) newTypes.bloodGlucose = true;
                if (normalizedTypeIds.includes(3)) newTypes.weight = true;
                setMonitoringTypes(newTypes);
                setMonitoringValues(newValues);

                // Insurance from `insurances` table (via insurance_details / insurance_data on API)
                const ins = p.insurance_details || p.insurance_data || p.insuranceDetails || {};
                setInsuranceData({
                    primary: {
                        insuranceName: ins.primary?.insuranceName || ins.primary?.insurance_name || '',
                        subscriberId: ins.primary?.subscriberId || ins.primary?.subscriber_id || '',
                        insuredName: ins.primary?.insuredName || ins.primary?.insured_name || '',
                        groupNo: ins.primary?.groupNo || ins.primary?.group_no || ''
                    },
                    secondary: {
                        insuranceName: ins.secondary?.insuranceName || ins.secondary?.insurance_name || '',
                        subscriberId: ins.secondary?.subscriberId || ins.secondary?.subscriber_id || '',
                        insuredName: ins.secondary?.insuredName || ins.secondary?.insured_name || '',
                        groupNo: ins.secondary?.groupNo || ins.secondary?.group_no || ''
                    }
                });

                const initialPayload = buildPatientUpdatePayload(
                    {
                        title: titleFromApi,
                        firstName: p.first_name || '',
                        middleInitial: middleFromApi,
                        lastName: p.last_name || '',
                        username: p.username || '',
                        addressLine1: p.address_line_1 || p.address?.address_line_1 || '',
                        addressLine2: p.address_line_2 || p.address?.address_line_2 || '',
                        city: p.city || p.address?.city || '',
                        state: stateSelectValueFromPatient(p),
                        zipCode: p.zip_code != null || p.address?.zip_code != null ? String(p.zip_code ?? p.address?.zip_code ?? '') : '',
                        homePhone: homeDigits ? formatPhoneDisplay(homeDigits) : '',
                        cellPhone: cellDigits ? formatPhoneDisplay(cellDigits) : '',
                        fax: p.fax || '',
                        email: p.email || '',
                        gender: genderValue,
                        dateOfBirth: p.date_of_birth ? p.date_of_birth.split('T')[0] : '',
                        twoWayAuth: p.is_two_way_auth === 1 ? 'Enabled' : 'Disabled',
                        status: statusValue,
                        timeZone: normalizeTimeZone(p.time_zone ?? p.timeZone),
                        rpmStartDate: p.rpm_start_date ? p.rpm_start_date.split('T')[0] : '',
                        provider: p.provider_id ? String(p.provider_id) : '',
                        systemCaregiverIds: expandCaregiverIdsFromApi(
                            p.system_caregiver_ids || p.systemCaregiverIds,
                            p.system_caregiver_id ?? p.system_caregiver?.id
                        ),
                        practiceCaregiverIds: (() => {
                            const fromJunction = expandCaregiverIdsFromApi(p.practice_caregiver_ids || p.practiceCaregiverIds, null);
                            const fromColumn = expandCaregiverIdsFromApi(null, p.caregiver_id);
                            const seen = new Set();
                            const out = [];
                            for (const id of [...fromJunction, ...fromColumn]) {
                                if (!id || seen.has(id)) continue;
                                seen.add(id);
                                out.push(String(id));
                            }
                            if (out.length > 0) return out;
                            const one = p.practice_caregiver_id ?? p.practiceCaregiver?.id;
                            return one != null && String(one).trim() !== '' ? [String(one)] : [];
                        })()
                    },
                    newTypes,
                    newValues,
                    {
                        primary: {
                            insuranceName: ins.primary?.insuranceName || ins.primary?.insurance_name || '',
                            subscriberId: ins.primary?.subscriberId || ins.primary?.subscriber_id || '',
                            insuredName: ins.primary?.insuredName || ins.primary?.insured_name || '',
                            groupNo: ins.primary?.groupNo || ins.primary?.group_no || ''
                        },
                        secondary: {
                            insuranceName: ins.secondary?.insuranceName || ins.secondary?.insurance_name || '',
                            subscriberId: ins.secondary?.subscriberId || ins.secondary?.subscriber_id || '',
                            insuredName: ins.secondary?.insuredName || ins.secondary?.insured_name || '',
                            groupNo: ins.secondary?.groupNo || ins.secondary?.group_no || ''
                        }
                    },
                    []
                );
                initialUpdatePayloadRef.current = initialPayload;
            } else {
                toast.error(pRes.data?.message || 'Patient not found');
                setPatientData(null);
            }

            // Extract data correctly from provider/caregiver API responses
            const providersData = normalizeArrayFromApiResponse(providersRes, 'providers');
            const caregiversData = normalizeArrayFromApiResponse(caregiversRes, 'caregivers');
            const sysCaregiversData = normalizeArrayFromApiResponse(sysRes, 'caregivers');

            setPracticeProviders(providersData);
            setPracticeCaregivers(caregiversData);
            setSystemCaregivers(sysCaregiversData);

            const practiceInfo = practiceRes?.data?.data || {};
            setPracticeMonitoringTypes(normalizePracticeMonitoringTypes(practiceInfo.selected_monitoring_types));
        } catch (err) {
            console.error('Error fetching data:', err);
            toast.error('Failed to load patient data');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setFieldErrors(prev => {
            const next = { ...prev };
            delete next[name];

            if (name === 'username') {
                const v = String(value || '').trim();
                if (!v) next.username = 'Username is required';
                else if (!/^[a-zA-Z0-9]+$/.test(v)) next.username = 'Username should only contain alphanumeric characters';
            }
            if (name === 'email') {
                const v = String(value || '').trim();
                if (!v) next.email = 'Email is required';
                else if (!isValidEmailFormat(v)) next.email = 'Invalid email format';
            }
            if (name === 'middleInitial') {
                const v = String(value || '');
                if (/\s/.test(v)) next.middleInitial = 'Middle Initial cannot contain spaces';
            }
            if (name === 'city') {
                const v = String(value || '').trim();
                if (v && !/^[a-zA-Z\s'-]+$/.test(v)) next.city = 'City may only contain letters, spaces, hyphens, and apostrophes';
            }
            if (name === 'zipCode') {
                const v = String(value || '').trim();
                if (v && v.length < 2) next.zipCode = 'Zip code must be at least 2 characters';
            }
            if (name === 'homePhone') {
                const digits = String(value || '').replace(/\D/g, '');
                if (digits && digits.length < 10) next.homePhone = 'Invalid phone format';
            }
            return next;
        });
    };

    const pickProviderField = (name, idStr) => {
        setFormData(prev => ({ ...prev, [name]: idStr }));
        if (name === 'provider') {
            setFieldErrors(prev => {
                if (!prev.provider) return prev;
                const next = { ...prev };
                delete next.provider;
                return next;
            });
        }
    };

    const addSystemCaregiverId = (idStr) => {
        setFormData(prev => {
            if (prev.systemCaregiverIds.includes(idStr)) return prev;
            return { ...prev, systemCaregiverIds: [...prev.systemCaregiverIds, idStr] };
        });
    };

    const removeSystemCaregiverId = (idStr) => {
        setFormData(prev => ({
            ...prev,
            systemCaregiverIds: prev.systemCaregiverIds.filter((id) => id !== idStr)
        }));
    };

    const addPracticeCaregiverId = (idStr) => {
        setFormData(prev => {
            if (prev.practiceCaregiverIds.includes(idStr)) return prev;
            return { ...prev, practiceCaregiverIds: [...prev.practiceCaregiverIds, idStr] };
        });
        setFieldErrors(prev => {
            if (!prev.practiceCaregiver) return prev;
            const next = { ...prev };
            delete next.practiceCaregiver;
            return next;
        });
    };

    const removePracticeCaregiverId = (idStr) => {
        setFormData(prev => ({
            ...prev,
            practiceCaregiverIds: prev.practiceCaregiverIds.filter((id) => id !== idStr)
        }));
    };

    const handleInsuranceChange = (type, field, value) => {
        setInsuranceData(prev => ({
            ...prev,
            [type]: { ...prev[type], [field]: value }
        }));
    };

    const handleUpdatePatient = async (e) => {
        e.preventDefault();
        const nextErr = {};
        if (!String(formData.firstName || '').trim()) nextErr.firstName = 'First Name is required';
        if (!String(formData.lastName || '').trim()) nextErr.lastName = 'Last Name is required';
        if (!String(formData.username || '').trim()) nextErr.username = 'Username is required';
        else if (!/^[a-zA-Z0-9]+$/.test(String(formData.username).trim())) nextErr.username = 'Username should only contain alphanumeric characters';
        if (!String(formData.addressLine1 || '').trim()) nextErr.addressLine1 = 'Address Line 1 is required';
        const emailValue = String(formData.email || '').trim();
        if (!emailValue) nextErr.email = 'Email is required';
        else if (!isValidEmailFormat(emailValue)) nextErr.email = 'Invalid email format';
        const cityTrim = String(formData.city || '').trim();
        if (!cityTrim) nextErr.city = 'City is required';
        else if (!/^[a-zA-Z\s'-]+$/.test(cityTrim)) nextErr.city = 'City may only contain letters, spaces, hyphens, and apostrophes';
        if (!String(formData.state || '').trim()) nextErr.state = 'State is required';
        const zipStr = String(formData.zipCode || '').trim();
        if (!zipStr) nextErr.zipCode = 'Zip Code is required';
        else if (zipStr.length < 2) nextErr.zipCode = 'Zip code must be at least 2 characters';
        const homePhoneDigits = String(formData.homePhone || '').replace(/\D/g, '');
        if (!homePhoneDigits) nextErr.homePhone = 'Home Phone is required';
        else if (homePhoneDigits.length < 10) nextErr.homePhone = 'Invalid phone format';
        if (!String(formData.timeZone || '').trim()) nextErr.timeZone = 'Time Zone is required';
        if (!String(formData.rpmStartDate || '').trim()) nextErr.rpmStartDate = 'RPM Start Date is required';
        if (!formData.provider || String(formData.provider).trim() === '') {
            nextErr.provider = 'Provider is required';
        }
        if (!formData.practiceCaregiverIds || formData.practiceCaregiverIds.length === 0) {
            nextErr.practiceCaregiver = 'Select at least one practice caregiver';
        }

        // Email Validation
        if (formData.email && /\s/.test(formData.email)) {
            nextErr.email = 'email id is invalid';
        }

        // Insurance Validation
        const insuranceRegex = /^[a-zA-Z\s&.-]+$/;
        const subscriberRegex = /^[a-zA-Z0-9-]+$/;
        const insuredNameRegex = /^[a-zA-Z\s'-]+$/;

        ['primary', 'secondary'].forEach(type => {
            const data = insuranceData[type];
            if (data.insuranceName && !insuranceRegex.test(data.insuranceName)) {
                nextErr[`${type}InsuranceName`] = 'Invalid Insurance Name';
            }
            if (data.subscriberId && !subscriberRegex.test(data.subscriberId)) {
                nextErr[`${type}SubscriberId`] = 'Invalid Subscriber ID';
            }
            if (data.insuredName && !insuredNameRegex.test(data.insuredName)) {
                nextErr[`${type}InsuredName`] = 'Invalid Insured Name';
            }
            if (data.groupNo && !subscriberRegex.test(data.groupNo)) {
                nextErr[`${type}GroupNo`] = 'Invalid Group Number';
            }
        });

        // Device Serial Number Validation
        const serialRegex = /^[a-zA-Z0-9-]+$/;
        mappedDevices.forEach((meter, idx) => {
            const serial = String(meter.serial_number || '').trim();
            if (serial) {
                if (!serialRegex.test(serial)) {
                    toast.error(`Invalid Serial for Device #${idx + 1}`);
                    nextErr[`meterSerial_${idx}`] = 'Invalid Serial';
                } else if (serial.length < 8 || serial.length > 16) {
                    toast.error(`Serial for Device #${idx + 1} must be 8-16 characters`);
                    nextErr[`meterSerial_${idx}`] = 'Too short/long';
                }
            }
        });

        if (Object.keys(nextErr).length > 0) {
            setFieldErrors(nextErr);
            toast.error('Please fix the highlighted fields');
            return;
        }
        setFieldErrors({});
        setIsSubmitting(true);
        try {
            const currentPayload = buildPatientUpdatePayload(
                formData,
                monitoringTypes,
                monitoringValues,
                insuranceData,
                mappedDevices
            );
            const originalPayload = initialUpdatePayloadRef.current || {};
            const changedPayload = diffUpdatePayload(originalPayload, currentPayload);

            if (Object.keys(changedPayload).length === 0) {
                toast.info('No changes to update');
                setIsSubmitting(false);
                return;
            }

            const data = new FormData();
            Object.entries(changedPayload).forEach(([key, value]) => {
                if (Array.isArray(value) || (value && typeof value === 'object')) {
                    data.append(key, JSON.stringify(value));
                } else if (value == null) {
                    data.append(key, '');
                } else {
                    data.append(key, String(value));
                }
            });

            const res = await patientService.updatePatient(practiceId, patientId, data);
            if (res.data.success) {
                toast.success('Patient information updated successfully');
                initialUpdatePayloadRef.current = currentPayload;
                try {
                    const metersRefresh = await patientService.getPatientMeters(practiceId, patientId);
                    setMappedDevices(normalizeMappedDeviceRows(metersRefresh.data?.data || []));
                } catch {
                    /* ignore */
                }
                try {
                    const docsRes = await patientService.getPatientDocuments(practiceId, patientId);
                    setUploadedDocuments(docsRes.data?.data || []);
                } catch {
                    /* ignore */
                }
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Update failed');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAddMeterToTable = () => {
        const modelNumber = String(meterForm.modelNumber || '').trim();
        const extension = String(meterForm.extension || '').trim();
        const serialNumber = String(meterForm.serialNumber || '').trim();
        if (!modelNumber || !extension || !serialNumber) {
            toast.error('Model, extension, and serial number are required');
            return;
        }
        if (serialNumber.length > 16) {
            toast.error('Serial number must not exceed 16 characters');
            return;
        }
        const row = {
            id: null,
            _localKey: `local-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            model_number: modelNumber,
            extension,
            serial_number: serialNumber,
            gateway_serial_number: String(meterForm.gatewaySerialNumber || '').trim()
        };
        setMappedDevices((prev) => [...prev, row]);
        setMeterForm({ modelNumber: '', extension: '', serialNumber: '', gatewaySerialNumber: '' });
        toast.success('Meter added to the list. Open Patient Info and click Update to save.');
    };

    const executeDeleteMeterRow = () => {
        const row = confirmDeleteMeterRow;
        if (!row) return;
        setConfirmDeleteMeterRow(null);
        setMappedDevices((prev) =>
            prev.filter((m) => (row.id != null ? m.id !== row.id : m._localKey !== row._localKey))
        );
    };

    const handleEditMeterRow = (row) => {
        setMeterForm({
            modelNumber: row.model_number || '',
            extension: row.extension || '',
            serialNumber: row.serial_number || '',
            gatewaySerialNumber: row.gateway_serial_number || ''
        });
        setMappedDevices((prev) =>
            prev.filter((m) => (row.id != null ? m.id !== row.id : m._localKey !== row._localKey))
        );
        toast.info('Row loaded into form above. Make changes and click Add meter.');
    };

    const handleUploadDoc = async (e) => {
        if (e && typeof e.preventDefault === 'function') e.preventDefault();
        if (!docUpload.file || !docUpload.name) {
            toast.error('Please provide document name and file');
            return;
        }
        try {
            const data = new FormData();
            data.append('document_name', docUpload.name);
            data.append('documents', docUpload.file);
            // Use updatePatient to upload the document
            const res = await patientService.updatePatient(practiceId, patientId, data);
            if (res.data.success) {
                toast.success('Document uploaded successfully');
                setDocUpload({ name: '', file: null });
                if (fileInputRef.current) fileInputRef.current.value = '';
                const docsRes = await patientService.getPatientDocuments(practiceId, patientId);
                setUploadedDocuments(docsRes.data?.data || []);
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to upload document');
        }
    };

    const executeDeleteDoc = async () => {
        const doc = confirmDeleteDoc;
        if (!doc?.id) return;
        const docId = doc.id;
        const previousList = uploadedDocuments;
        setConfirmDeleteDoc(null);
        setUploadedDocuments((prev) => prev.filter((d) => d.id !== docId));
        toast.success('Document deleted successfully');
        try {
            const res = await patientService.deletePatientDocument(practiceId, patientId, docId);
            if (!res.data.success) {
                setUploadedDocuments(previousList);
                toast.error(res.data?.message || 'Failed to delete document');
            }
        } catch (err) {
            setUploadedDocuments(previousList);
            toast.error(err.response?.data?.message || 'Failed to delete document');
        }
    };

    const handleDocFileChange = (e) => {
        const selectedFile = e?.target?.files?.[0];
        if (!selectedFile) return;

        // Instant Validation: Type
        const allowedMimes = [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'text/plain',
            'image/jpeg',
            'image/png',
            'application/xml',
            'text/xml'
        ];
        if (!allowedMimes.includes(selectedFile.type)) {
            toast.error('Invalid document type. Allowed: PDF, Word, TXT, JPG, PNG, XML.');
            e.target.value = ''; // Reset input
            return;
        }

        // Instant Validation: Size (25MB)
        const maxSize = 25 * 1024 * 1024;
        if (selectedFile.size > maxSize) {
            toast.error('File is too large. Maximum size allowed is 25MB.');
            e.target.value = ''; // Reset input
            return;
        }

        setDocUpload((prev) => ({ ...prev, file: selectedFile }));
    };

    if (loading) {
        return (
            <div className="p-edit-container">
                <PageLoadingSpinner minHeight="70vh" />
            </div>
        );
    }

    const patientDisplayName = `${patientData?.last_name || ''}${patientData?.last_name ? ', ' : ''}${patientData?.first_name || ''}`.trim() || 'Patient';

    const openDoc = (doc) => {
        const file = doc?.document || doc?.file_name || doc?.filename;
        if (!file) return;
        window.open(`/uploads/patient-documents/${file}`, '_blank', 'noreferrer');
    };

    const renderUploadedDocumentsTable = (viewOnlyTable) => (
        <table className="table table-hover mb-0" style={{ fontSize: '14px', width: '100%', tableLayout: 'fixed' }}>
            <thead style={{ backgroundColor: '#f8f9fa' }}>
                <tr>
                    <th style={{ width: '50px', paddingLeft: '15px', textAlign: 'left' }}>#</th>
                    <th style={{ textAlign: 'left' }}>Document Name</th>
                    <th style={{ width: '20%', textAlign: 'left' }}>Uploaded Date</th>
                    <th style={{ width: '20%', textAlign: 'left' }}>Uploaded By</th>
                    <th style={{ width: '120px', paddingRight: '15px', textAlign: 'center' }}>Action</th>
                </tr>
            </thead>
            <tbody>
                {uploadedDocuments.length > 0 ? (
                    uploadedDocuments.map((doc, idx) => (
                        <tr key={doc.id}>
                            <td style={{ paddingLeft: '15px', textAlign: 'left', verticalAlign: 'middle' }}>{idx + 1}</td>
                            <td style={{ textAlign: 'left', verticalAlign: 'middle', wordBreak: 'break-all' }}>
                                {doc.document_name || doc.document || doc.file_name || doc.filename || 'Untitled Document'}
                            </td>
                            <td style={{ textAlign: 'left', verticalAlign: 'middle' }}>{formatDate(doc.created_at)}</td>
                            <td style={{ textAlign: 'left', verticalAlign: 'middle' }}>{doc.uploaded_by_name || 'System'}</td>
                            <td style={{ paddingRight: '15px', textAlign: 'center', verticalAlign: 'middle' }}>
                                <div className="d-flex justify-content-center">
                                    <ActionButtons
                                        hideEdit
                                        onView={() => openDoc(doc)}
                                        onDelete={() => setConfirmDeleteDoc(doc)}
                                    />
                                </div>
                            </td>
                        </tr>
                    ))
                ) : (
                    <tr><td colSpan="5" className="text-center py-4 text-muted">No documents uploaded</td></tr>
                )}
            </tbody>
        </table>
    );

    const renderUploadedDocumentsBody = ({ showUpload = false, viewOnlyTable = false } = {}) => (
        <div className="add-patient-section-wrapper mt-4">
            <div className="add-patient-section-header">
                <h3>Documents</h3>
            </div>
            <div className="add-patient-section-content p-0" style={{ overflow: 'hidden' }}>
                {renderUploadedDocumentsTable(viewOnlyTable)}
            </div>
            {showUpload ? (
                <div className="add-patient-documents-row mt-3 px-1 pb-1">
                    <div className="add-patient-doc-col-filename">
                        <label>File Name</label>
                        <input type="text" className="add-patient-form-control" value={docUpload.name} onChange={(e) => setDocUpload({ ...docUpload, name: e.target.value })} />
                    </div>
                    <div className="add-patient-doc-col-upload">
                        <label>Upload File</label>
                        <div className="add-patient-upload-group-combined">
                            <input
                                type="text"
                                className="add-patient-form-control add-patient-file-display-input"
                                readOnly
                                placeholder="No file selected"
                                value={docUpload.file ? docUpload.file.name : ''}
                            />
                            <label className="add-patient-btn-upload-right">
                                <FontAwesomeIcon icon={faCloudUploadAlt} /> Upload File
                                <input
                                    type="file"
                                    hidden
                                    ref={fileInputRef}
                                    onChange={handleDocFileChange}
                                    accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.xml"
                                />
                            </label>
                            <button type="button" className="add-patient-btn-icon-dark" title="Save upload" onClick={handleUploadDoc}>
                                <FontAwesomeIcon icon={faSave} />
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );

    return (
        <>
        <div className="p-edit-container">
            <div className="p-edit-single-card">
            <div className="p-edit-header">
                <div className="p-edit-title-box">
                    <div className="d-flex align-items-center gap-3">
                        {patientProfilePicUrl && (
                            <div className="p-edit-profile-pic-wrap">
                                <img
                                    src={patientProfilePicUrl}
                                    alt={patientDisplayName}
                                    className="p-edit-profile-pic-img"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                />
                            </div>
                        )}
                        <div>
                            <h2>{patientDisplayName}</h2>
                            <div className="d-flex align-items-center gap-3">
                                <p className="mb-0">{patientData?.practice_name ? `Practice: ${patientData.practice_name}` : ''}</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="d-flex align-items-center gap-3">
                    {practiceLogoUrl && !practiceLogoFailed ? (
                        <div className="p-edit-header-logo-wrap">
                            <img
                                src={practiceLogoUrl}
                                alt={patientData?.practice_name ? `${patientData.practice_name} logo` : 'Practice logo'}
                                className="p-edit-header-logo-img"
                                onError={() => setPracticeLogoFailed(true)}
                            />
                        </div>
                    ) : (
                        <div className="p-edit-header-logo-placeholder" aria-hidden />
                    )}
                    {formData.accountNumber && (
                        <span style={{ color: '#2c2c2c', fontSize: '14px', fontWeight: 600, backgroundColor: '#f0f4f8', padding: '4px 10px', borderRadius: '4px' }}>
                            Account #: {formData.accountNumber}
                        </span>
                    )}
                </div>
            </div>

            <div className="p-edit-tabs">
                <button type="button" className={`p-edit-tab ${activeTab === 'patient-info' ? 'active' : ''}`} onClick={() => setActiveTab('patient-info')}>
                    <FontAwesomeIcon icon={faPencilSquare} /> Patient Info
                </button>
                <button type="button" className={`p-edit-tab ${activeTab === 'schedule' ? 'active' : ''}`} onClick={() => setActiveTab('schedule')}>
                    <FontAwesomeIcon icon={faGear} /> Schedule & Target Settings
                </button>
                <button type="button" className={`p-edit-tab ${activeTab === 'meter-mapping' ? 'active' : ''}`} onClick={() => setActiveTab('meter-mapping')}>
                    <FontAwesomeIcon icon={faMicrochip} /> Meter Mapping
                </button>
                <button type="button" className={`p-edit-tab ${activeTab === 'uploaded-documents' ? 'active' : ''}`} onClick={() => setActiveTab('uploaded-documents')}>
                    <FontAwesomeIcon icon={faFileLines} /> Uploaded Documents
                </button>
            </div>

            <div className="p-edit-content">
                {activeTab === 'event-logs' && (
                    <div className="p-edit-tab-pane">
                        <div className="d-flex align-items-center mb-4 pb-3" style={{ borderBottom: '1px solid #eee' }}>
                            <button type="button" className="p-edit-btn-event-log" onClick={() => setActiveTab(previousTab || 'patient-info')} style={{ width: 'auto', padding: '0 16px' }}>
                                <FontAwesomeIcon icon={faArrowLeft} /> Back
                            </button>
                            <h3 className="mb-0 ms-3" style={{ fontSize: '1.2rem', color: '#057a7a', fontWeight: 600 }}>Event Logs - {patientsLogModule}</h3>
                        </div>
                        <PatientsLogContent
                            show={true}
                            moduleId={patientsLogModule}
                            section={patientsLogSection}
                            patientId={patientId != null && String(patientId).trim() !== '' ? parseInt(String(patientId), 10) || patientId : null}
                            practiceId={practiceId != null && String(practiceId).trim() !== '' ? parseInt(String(practiceId), 10) || practiceId : null}
                        />
                    </div>
                )}

                {activeTab === 'patient-info' && (
                    <div className="p-edit-tab-pane p-edit-patient-info-tab">
                        <div className="add-patient-form-card p-edit-patient-info-pane">
                            <form onSubmit={handleUpdatePatient} className="add-patient-provider-form">
                                <h5 className="add-patient-block-title p-edit-block-title-first">Patient information</h5>
                                <div className="add-patient-block">
                                <div className="add-patient-row-pair">
                                    <div className="add-patient-col-half">
                                        <div className="d-flex gap-3">
                                            <div className="add-patient-form-group" style={{ flex: '0 0 100px', minWidth: '100px' }}>
                                                <label htmlFor="title">Title</label>
                                                <select id="title" name="title" value={formData.title} onChange={handleInputChange} className="add-patient-form-control">
                                                    <option value="">- Select -</option>
                                                    <option value="1">Dr.</option>
                                                    <option value="2">Mr.</option>
                                                    <option value="3">Mrs.</option>
                                                    <option value="4">Ms.</option>
                                                </select>
                                            </div>
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="firstName">First Name <span className="add-patient-required">*</span></label>
                                                <input id="firstName" type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} required className={`add-patient-form-control ${fieldErrors.firstName ? 'is-invalid' : ''}`} />
                                                {fieldErrors.firstName && <div className="add-patient-error-message">{fieldErrors.firstName}</div>}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="add-patient-col-half">
                                        <div className="add-patient-form-group">
                                            <label htmlFor="username">Username <span className="add-patient-required">*</span></label>
                                            <input id="username" type="text" name="username" value={formData.username} onChange={handleInputChange} required className={`add-patient-form-control ${fieldErrors.username ? 'is-invalid' : ''}`} />
                                            {fieldErrors.username && <div className="add-patient-error-message">{fieldErrors.username}</div>}
                                        </div>
                                    </div>
                                </div>

                                <div className="add-patient-row-pair">
                                    <div className="add-patient-col-half">
                                        <div className="d-flex gap-3">
                                            <div className="add-patient-form-group" style={{ flex: '0 0 100px' }}>
                                                <label htmlFor="middleInitial">Middle Initial</label>
                                                <input id="middleInitial" type="text" name="middleInitial" value={formData.middleInitial} onChange={handleInputChange} className={`add-patient-form-control ${fieldErrors.middleInitial ? 'is-invalid' : ''}`} maxLength="1" />
                                                {fieldErrors.middleInitial && <div className="add-patient-error-message">{fieldErrors.middleInitial}</div>}
                                            </div>
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="lastName">Last Name <span className="add-patient-required">*</span></label>
                                                <input id="lastName" type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} required className={`add-patient-form-control ${fieldErrors.lastName ? 'is-invalid' : ''}`} />
                                                {fieldErrors.lastName && <div className="add-patient-error-message">{fieldErrors.lastName}</div>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="add-patient-col-half">
                                        <div className="add-patient-form-group">
                                            <label htmlFor="addressLine1">Address Line 1 <span className="add-patient-required">*</span></label>
                                            <input id="addressLine1" type="text" name="addressLine1" value={formData.addressLine1} onChange={handleInputChange} className={`add-patient-form-control ${fieldErrors.addressLine1 ? 'is-invalid' : ''}`} />
                                            {fieldErrors.addressLine1 && <div className="add-patient-error-message">{fieldErrors.addressLine1}</div>}
                                        </div>
                                    </div>
                                </div>

                                <div className="add-patient-row-pair">
                                    <div className="add-patient-col-half">
                                        <div className="add-patient-form-group">
                                            <label htmlFor="addressLine2">Address Line 2</label>
                                            <input id="addressLine2" type="text" name="addressLine2" value={formData.addressLine2} onChange={handleInputChange} className="add-patient-form-control" />
                                        </div>
                                    </div>
                                    <div className="add-patient-col-half">
                                        <div className="add-patient-form-group">
                                            <label htmlFor="email">Email <span className="add-patient-required">*</span></label>
                                            <input id="email" type="email" name="email" value={formData.email} onChange={handleInputChange} required className={`add-patient-form-control ${fieldErrors.email ? 'is-invalid' : ''}`} />
                                            {fieldErrors.email && <div className="add-patient-error-message">{fieldErrors.email}</div>}
                                        </div>
                                    </div>
                                </div>

                                <div className="add-patient-row-pair">
                                    <div className="add-patient-col-half">
                                        <div className="d-flex gap-3">
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="city">City <span className="add-patient-required">*</span></label>
                                                <input id="city" type="text" name="city" value={formData.city} onChange={handleInputChange} className={`add-patient-form-control ${fieldErrors.city ? 'is-invalid' : ''}`} />
                                                {fieldErrors.city && <div className="add-patient-error-message">{fieldErrors.city}</div>}
                                            </div>
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="state">State <span className="add-patient-required">*</span></label>
                                                <select id="state" name="state" value={formData.state} onChange={handleInputChange} className={`add-patient-form-control ${fieldErrors.state ? 'is-invalid' : ''}`}>
                                                    <option value="">- Select -</option>
                                                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                                                </select>
                                                {fieldErrors.state && <div className="add-patient-error-message">{fieldErrors.state}</div>}
                                            </div>
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="zipCode">Zip Code <span className="add-patient-required">*</span></label>
                                                <input id="zipCode" type="text" name="zipCode" value={formData.zipCode} onChange={handleInputChange} className={`add-patient-form-control ${fieldErrors.zipCode ? 'is-invalid' : ''}`} />
                                                {fieldErrors.zipCode && <div className="add-patient-error-message">{fieldErrors.zipCode}</div>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="add-patient-col-half">
                                        <div className="d-flex gap-3">
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="homePhone">Home Phone <span className="add-patient-required">*</span></label>
                                                <input id="homePhone" type="tel" name="homePhone" value={formData.homePhone} onChange={handleInputChange} className={`add-patient-form-control ${fieldErrors.homePhone ? 'is-invalid' : ''}`} />
                                                {fieldErrors.homePhone && <div className="add-patient-error-message">{fieldErrors.homePhone}</div>}
                                            </div>
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="cellPhone">Cell Phone</label>
                                                <input id="cellPhone" type="tel" name="cellPhone" value={formData.cellPhone} onChange={handleInputChange} className="add-patient-form-control" />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="add-patient-row-pair">
                                    <div className="add-patient-col-half">
                                        <div className="d-flex gap-3">
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="gender">Gender</label>
                                                <select id="gender" name="gender" value={formData.gender} onChange={handleInputChange} className="add-patient-form-control">
                                                    <option value="Male">Male</option>
                                                    <option value="Female">Female</option>
                                                    <option value="Other">Other</option>
                                                </select>
                                            </div>
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="dateOfBirth">Date of Birth</label>
                                                <input id="dateOfBirth" type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleInputChange} className="add-patient-form-control" />
                                            </div>
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="fax">Fax</label>
                                                <input id="fax" type="tel" name="fax" value={formData.fax} onChange={handleInputChange} className="add-patient-form-control" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="add-patient-col-half">
                                        <div className="d-flex gap-3">
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="twoWayAuth">Two Way Authentication</label>
                                                <select id="twoWayAuth" name="twoWayAuth" value={formData.twoWayAuth} onChange={handleInputChange} className="add-patient-form-control">
                                                    <option value="Enabled">Enabled</option>
                                                    <option value="Disabled">Disabled</option>
                                                </select>
                                            </div>
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="status">Status</label>
                                                <select id="status" name="status" value={formData.status} onChange={handleInputChange} className="add-patient-form-control">
                                                    <option value="Active">Active</option>
                                                    <option value="Pending">Pending</option>
                                                    <option value="Locked">Locked</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="add-patient-row-pair">
                                    <div className="add-patient-col-half">
                                        <div className="d-flex gap-3">
                                            <div className="flex-grow-1">
                                                <PatientAscStyleMultiPicker
                                                    label="Practice caregivers"
                                                    required
                                                    selectedIds={formData.practiceCaregiverIds}
                                                    options={practiceCaregiverPickerOptions}
                                                    onAddId={addPracticeCaregiverId}
                                                    onRemoveId={removePracticeCaregiverId}
                                                    error={fieldErrors.practiceCaregiver}
                                                    placeholder="Select practice caregivers"
                                                    emptyMessage="No practice caregivers available"
                                                />
                                            </div>
                                            <div className="flex-grow-1" />
                                        </div>
                                    </div>
                                    <div className="add-patient-col-half">
                                        <div className="d-flex gap-3">
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="timeZone">Time Zone <span className="add-patient-required">*</span></label>
                                                <select id="timeZone" name="timeZone" value={formData.timeZone} onChange={handleInputChange} className={`add-patient-form-control ${fieldErrors.timeZone ? 'is-invalid' : ''}`}>
                                                    <option value="">- Select -</option>
                                                    {TIME_ZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                                                </select>
                                                {fieldErrors.timeZone && <div className="add-patient-error-message">{fieldErrors.timeZone}</div>}
                                            </div>
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="rpmStartDate">RPM Start Date <span className="add-patient-required">*</span></label>
                                                <input id="rpmStartDate" type="date" name="rpmStartDate" value={formData.rpmStartDate} onChange={handleInputChange} className={`add-patient-form-control ${fieldErrors.rpmStartDate ? 'is-invalid' : ''}`} />
                                                {fieldErrors.rpmStartDate && <div className="add-patient-error-message">{fieldErrors.rpmStartDate}</div>}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="add-patient-row-pair">
                                    <div className="add-patient-col-half" />
                                    <div className="add-patient-col-half">
                                        <div className="d-flex gap-3">
                                            <div style={{ flex: '1 1 0', minWidth: 0 }}>
                                                <PatientAscStyleSinglePicker
                                                    label="Provider"
                                                    name="provider"
                                                    required
                                                    value={formData.provider}
                                                    options={practiceProviders}
                                                    error={fieldErrors.provider}
                                                    placeholder="Select provider"
                                                    emptyMessage="No providers available"
                                                    onPick={pickProviderField}
                                                />
                                            </div>
                                            {/* System Caregiver picker disabled
                                            <div style={{ flex: '1 1 0', minWidth: 0 }}>
                                                <PatientAscStyleMultiPicker
                                                    label="System caregivers"
                                                    required={false}
                                                    selectedIds={formData.systemCaregiverIds}
                                                    options={systemCaregiverPickerOptions}
                                                    onAddId={addSystemCaregiverId}
                                                    onRemoveId={removeSystemCaregiverId}
                                                    error={undefined}
                                                    placeholder="Select system caregivers"
                                                    emptyMessage="No system caregivers available"
                                                />
                                            </div>
                                            */}
                                        </div>
                                    </div>
                                </div>

                                <div className="add-patient-section-wrapper p-edit-section-in-form">
                                    <div className="add-patient-section-header"><h3>Monitoring type</h3></div>
                                    <div className="add-patient-section-content add-patient-monitoring-content">
                                        {practiceMonitoringTypes.includes(1) && (
                                            <div className="add-patient-monitoring-row">
                                                <div className="add-patient-monitoring-check-col">
                                                    <input type="checkbox" id="m-bp" checked={monitoringTypes.bloodPressure} onChange={() => setMonitoringTypes(prev => ({ ...prev, bloodPressure: !prev.bloodPressure }))} />
                                                    <label htmlFor="m-bp">Blood Pressure</label>
                                                </div>
                                                <div className="add-patient-monitoring-select-col">
                                                    <input className="add-patient-form-control" value={monitoringValues.bloodPressure} onChange={(e) => setMonitoringValues(prev => ({ ...prev, bloodPressure: e.target.value }))} placeholder="ICD Code" disabled={!monitoringTypes.bloodPressure} />
                                                </div>
                                            </div>
                                        )}
                                        {practiceMonitoringTypes.includes(2) && (
                                            <div className="add-patient-monitoring-row">
                                                <div className="add-patient-monitoring-check-col">
                                                    <input type="checkbox" id="m-bg" checked={monitoringTypes.bloodGlucose} onChange={() => setMonitoringTypes(prev => ({ ...prev, bloodGlucose: !prev.bloodGlucose }))} />
                                                    <label htmlFor="m-bg">Blood Glucose</label>
                                                </div>
                                                <div className="add-patient-monitoring-select-col">
                                                    <input className="add-patient-form-control" value={monitoringValues.bloodGlucose} onChange={(e) => setMonitoringValues(prev => ({ ...prev, bloodGlucose: e.target.value }))} placeholder="ICD Code" disabled={!monitoringTypes.bloodGlucose} />
                                                </div>
                                            </div>
                                        )}
                                        {practiceMonitoringTypes.includes(3) && (
                                            <div className="add-patient-monitoring-row">
                                                <div className="add-patient-monitoring-check-col">
                                                    <input type="checkbox" id="m-wt" checked={monitoringTypes.weight} onChange={() => setMonitoringTypes(prev => ({ ...prev, weight: !prev.weight }))} />
                                                    <label htmlFor="m-wt">Weight</label>
                                                </div>
                                                <div className="add-patient-monitoring-select-col">
                                                    <input className="add-patient-form-control" value={monitoringValues.weight} onChange={(e) => setMonitoringValues(prev => ({ ...prev, weight: e.target.value }))} placeholder="ICD Code" disabled={!monitoringTypes.weight} />
                                                </div>
                                            </div>
                                        )}
                                        {practiceMonitoringTypes.length === 0 && (
                                            <div className="alert alert-warning" style={{width: '100%'}}>No monitoring types configured for this practice. Please configure monitoring types in practice settings.</div>
                                        )}
                                    </div>
                                </div>

                                <div className="add-patient-section-wrapper p-edit-section-in-form">
                                    <div className="add-patient-section-header"><h3>Insurance</h3></div>
                                    <div className="add-patient-section-content">
                                        <div className="add-patient-insurance-grid">
                                            <div className="add-patient-insurance-label-col"></div>
                                            <div className="add-patient-insurance-header-col"><label>Insurance Name</label></div>
                                            <div className="add-patient-insurance-header-col"><label>Subscriber Id</label></div>
                                            <div className="add-patient-insurance-header-col"><label>Insured's Name</label></div>
                                            <div className="add-patient-insurance-header-col"><label>Group No.</label></div>

                                            <div className="add-patient-insurance-label-col label-text">Primary</div>
                                            <div className="add-patient-insurance-input-col"><input type="text" className="add-patient-form-control" value={insuranceData.primary.insuranceName} onChange={(e) => handleInsuranceChange('primary', 'insuranceName', e.target.value)} /></div>
                                            <div className="add-patient-insurance-input-col"><input type="text" className="add-patient-form-control" value={insuranceData.primary.subscriberId} onChange={(e) => handleInsuranceChange('primary', 'subscriberId', e.target.value)} /></div>
                                            <div className="add-patient-insurance-input-col"><input type="text" className="add-patient-form-control" value={insuranceData.primary.insuredName} onChange={(e) => handleInsuranceChange('primary', 'insuredName', e.target.value)} /></div>
                                            <div className="add-patient-insurance-input-col"><input type="text" className="add-patient-form-control" value={insuranceData.primary.groupNo} onChange={(e) => handleInsuranceChange('primary', 'groupNo', e.target.value)} /></div>

                                            <div className="add-patient-insurance-label-col label-text">Secondary</div>
                                            <div className="add-patient-insurance-input-col"><input type="text" className="add-patient-form-control" value={insuranceData.secondary.insuranceName} onChange={(e) => handleInsuranceChange('secondary', 'insuranceName', e.target.value)} /></div>
                                            <div className="add-patient-insurance-input-col"><input type="text" className="add-patient-form-control" value={insuranceData.secondary.subscriberId} onChange={(e) => handleInsuranceChange('secondary', 'subscriberId', e.target.value)} /></div>
                                            <div className="add-patient-insurance-input-col"><input type="text" className="add-patient-form-control" value={insuranceData.secondary.insuredName} onChange={(e) => handleInsuranceChange('secondary', 'insuredName', e.target.value)} /></div>
                                            <div className="add-patient-insurance-input-col"><input type="text" className="add-patient-form-control" value={insuranceData.secondary.groupNo} onChange={(e) => handleInsuranceChange('secondary', 'groupNo', e.target.value)} /></div>
                                        </div>
                                    </div>
                                </div>

                                <div className="add-patient-section-wrapper add-patient-section-wrapper--in-block mt-3">
                                    <div className="add-patient-section-header"><h3>Uploaded documents</h3></div>
                                    <div className="add-patient-section-content p-0">
                                        {renderUploadedDocumentsBody({ showUpload: true, viewOnlyTable: false })}
                                    </div>
                                </div>
                                </div>

                                <div className="add-patient-form-actions p-edit-form-actions-row">
                                    <button
                                        type="button"
                                        className="p-edit-btn-event-log"
                                        onClick={() => openPatientsLog(LOG_MODULE_PATIENT)}
                                    >
                                        <FontAwesomeIcon icon={faHistory} /> Event log
                                    </button>
                                    <div className="p-edit-form-actions-right">
                                        <button
                                            type="button"
                                            className="add-patient-btn add-patient-btn-cancel"
                                            onClick={() => navigate(-1)}
                                            disabled={isSubmitting}
                                        >
                                            Cancel
                                        </button>
                                        <button type="submit" className="add-patient-btn add-patient-btn-add" disabled={isSubmitting}>
                                            {isSubmitting ? (<><FontAwesomeIcon icon={faSpinner} spin /> Updating...</>) : (<><FontAwesomeIcon icon={faSave} /> Update</>)}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {activeTab === 'schedule' && (
                    <div className="p-edit-tab-pane p-edit-tab-with-log-footer">
                        <div className="p-edit-schedule-scroll">
                            <ScheduleTargetSettings
                                practiceId={practiceId}
                                patientId={patientId}
                                showSectionEventLogButtons
                                onOpenEventLog={(section) =>
                                    openPatientsLog(LOG_MODULE_SCHEDULE, section || null)
                                }
                            />
                        </div>
                    </div>
                )}

                {activeTab === 'meter-mapping' && (
                    <div className="p-edit-tab-pane">
                        <div className="add-patient-form-card p-edit-addpatient-card p-edit-inner-pane" style={{ paddingTop: '10px' }}>

                            <div className="add-patient-section-wrapper mt-4">
                                <div className="add-patient-section-header">
                                    <h3>Meter Mapping</h3>
                                </div>
                                <div className="add-patient-section-content">
                                    <div className="add-patient-meter-mapping-row">
                                        <div className="add-patient-form-group">
                                            <label>Model number</label>
                                            <select value={meterForm.modelNumber} onChange={(e) => setMeterForm({ ...meterForm, modelNumber: e.target.value })} className="add-patient-form-control">
                                                <option value="">- Select -</option>
                                                {products.map(p => <option key={p.id || p.model_number} value={p.model_number}>{p.model_number}{p.product_name ? ` (${p.product_name})` : ''}</option>)}
                                            </select>
                                        </div>
                                        <div className="add-patient-form-group">
                                            <label>Extension</label>
                                            <select value={METER_EXTENSION_OPTIONS.some(o => String(o.value) === String(meterForm.extension)) ? String(meterForm.extension) : ''} onChange={(e) => setMeterForm({ ...meterForm, extension: e.target.value })} className="add-patient-form-control">
                                                <option value="">- Select -</option>
                                                {METER_EXTENSION_OPTIONS.map(ext => (
                                                    <option key={ext.value} value={ext.value}>{ext.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="add-patient-form-group">
                                            <label>Serial number</label>
                                            <input type="text" value={meterForm.serialNumber} onChange={(e) => setMeterForm({ ...meterForm, serialNumber: e.target.value.replace(/[^a-zA-Z0-9-]/g, '').slice(0, 16) })} className="add-patient-form-control" placeholder="Enter serial number" maxLength={16} />
                                        </div>
                                        <div className="add-patient-form-group">
                                            <label>Gateway serial number</label>
                                            <input type="text" value={meterForm.gatewaySerialNumber} onChange={(e) => setMeterForm({ ...meterForm, gatewaySerialNumber: e.target.value })} className="add-patient-form-control" />
                                        </div>
                                        <div className="add-patient-action-group">
                                            <label>&nbsp;</label>
                                            <button type="button" className="add-patient-btn add-patient-btn-add" onClick={handleAddMeterToTable}>
                                                <FontAwesomeIcon icon={faPlus} className="me-2" />
                                                Add meter
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="add-patient-section-wrapper mt-4">
                                <div className="add-patient-section-header">
                                    <h3>Mapped Devices</h3>
                                </div>
                                <div className="add-patient-section-content p-0" style={{ overflow: 'hidden' }}>
                                    <table className="table table-hover mb-0" style={{ fontSize: '14px', width: '100%', tableLayout: 'fixed' }}>
                                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                                            <tr>
                                                <th style={{ width: '50px', paddingLeft: '15px', textAlign: 'left' }}>#</th>
                                                <th style={{ width: '20%', textAlign: 'left' }}>Model No</th>
                                                <th style={{ width: '15%', textAlign: 'left' }}>Extension</th>
                                                <th style={{ width: '25%', textAlign: 'left' }}>Serial No</th>
                                                <th style={{ width: '25%', textAlign: 'left' }}>Gateway No</th>
                                                <th style={{ width: '120px', paddingRight: '15px', textAlign: 'center' }}>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {mappedDevices.length > 0 ? (
                                                mappedDevices.map((m, idx) => (
                                                    <tr key={m.id != null ? `meter-${m.id}` : m._localKey || `meter-${idx}`}>
                                                        <td style={{ paddingLeft: '15px', textAlign: 'left', verticalAlign: 'middle' }}>{idx + 1}</td>
                                                        <td style={{ textAlign: 'left', verticalAlign: 'middle' }}>{m.model_number || 'N/A'}</td>
                                                        <td style={{ textAlign: 'left', verticalAlign: 'middle' }}>{m.extension || 'N/A'}</td>
                                                        <td style={{ textAlign: 'left', verticalAlign: 'middle' }}>{m.serial_number || 'N/A'}</td>
                                                        <td style={{ textAlign: 'left', verticalAlign: 'middle' }}>{m.gateway_serial_number || 'N/A'}</td>
                                                        <td style={{ paddingRight: '15px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                            <div className="d-flex justify-content-center">
                                                                <ActionButtons
                                                                    hideView
                                                                    onEdit={() => handleEditMeterRow(m)}
                                                                    onDelete={() => setConfirmDeleteMeterRow(m)}
                                                                />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr><td colSpan="6" className="text-center py-4 text-muted">No devices mapped</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="p-edit-section-event-log-bar">
                                <button type="button" className="p-edit-btn-event-log" onClick={() => openPatientsLog(LOG_MODULE_METER)}>
                                    <FontAwesomeIcon icon={faHistory} /> Event log
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'uploaded-documents' && (
                    <div className="p-edit-tab-pane">
                        <div className="add-patient-form-card p-edit-addpatient-card p-edit-inner-pane" style={{ paddingTop: '16px' }}>
                            <div style={{ padding: 0 }}>
                                {renderUploadedDocumentsBody({ showUpload: false, viewOnlyTable: true })}
                            </div>
                            <div className="p-edit-section-event-log-bar">
                                <button type="button" className="p-edit-btn-event-log" onClick={() => openPatientsLog(LOG_MODULE_DOCUMENT)}>
                                    <FontAwesomeIcon icon={faHistory} /> Event log
                                </button>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
        </div>

        {confirmDeleteMeterRow && (
            <div className="practices-list-modal-overlay">
                <div className="practices-list-modal-container">
                    <div className="practices-list-modal-header">
                        <h5 className="practices-list-modal-title">Confirm Delete</h5>
                        <button
                            type="button"
                            className="practices-list-modal-close"
                            onClick={() => setConfirmDeleteMeterRow(null)}
                            aria-label="Close"
                        >
                            ×
                        </button>
                    </div>
                    <div className="practices-list-modal-body">
                        <p>
                            Remove this device from the list? Click <strong>Update</strong> on Patient Info to save
                            changes.
                        </p>
                        <p>
                            <strong>{confirmDeleteMeterRow.model_number || '—'}</strong> — Ext{' '}
                            {confirmDeleteMeterRow.extension || '—'} — Serial{' '}
                            <strong>{confirmDeleteMeterRow.serial_number || '—'}</strong>
                        </p>
                        <p className="practices-list-modal-warning">This action cannot be undone.</p>
                    </div>
                    <div className="practices-list-modal-footer">
                        <button
                            type="button"
                            className="practices-list-btn-cancel"
                            onClick={() => setConfirmDeleteMeterRow(null)}
                        >
                            Cancel
                        </button>
                        <button type="button" className="practices-list-btn-delete" onClick={executeDeleteMeterRow}>
                            Delete
                        </button>
                    </div>
                </div>
            </div>
        )}

        {confirmDeleteDoc && (
            <div className="practices-list-modal-overlay">
                <div className="practices-list-modal-container">
                    <div className="practices-list-modal-header">
                        <h5 className="practices-list-modal-title">Confirm Delete</h5>
                        <button
                            type="button"
                            className="practices-list-modal-close"
                            onClick={() => setConfirmDeleteDoc(null)}
                            aria-label="Close"
                        >
                            ×
                        </button>
                    </div>
                    <div className="practices-list-modal-body">
                        <p>
                            Are you sure you want to delete the document{' '}
                            <strong>
                                {confirmDeleteDoc.document_name ||
                                    confirmDeleteDoc.name ||
                                    confirmDeleteDoc.file_name ||
                                    confirmDeleteDoc.document ||
                                    'this file'}
                            </strong>
                            ?
                        </p>
                        <p className="practices-list-modal-warning">This action cannot be undone.</p>
                    </div>
                    <div className="practices-list-modal-footer">
                        <button
                            type="button"
                            className="practices-list-btn-cancel"
                            onClick={() => setConfirmDeleteDoc(null)}
                        >
                            Cancel
                        </button>
                        <button type="button" className="practices-list-btn-delete" onClick={executeDeleteDoc}>
                            Delete
                        </button>
                    </div>
                </div>
            </div>
        )}
        </>
    );
}
